// Copyright (c) Ping. All rights reserved.
//
// Slack-mrkdwn parser for the message-tile renderer. This is the
// RECEIVER complement of the composer's serializer
// (`@ping/enriched-text-input/src/markdown/serializer.ts`): it parses
// the EXACT same wire dialect the composer emits, so a formatted
// message renders the way it was typed instead of leaking raw markup.
//
// Dialect (Slack `mrkdwn`, NOT standard Markdown — see
// `docs/research/slack-composer/02-rich-text-formatting-markdown.md`):
//   • Bold          → *single asterisk*
//   • Italic        → _underscore_
//   • Strikethrough → ~single tilde~
//   • Inline code   → `backtick` (no inner markup)
//   • Link          → [label](url)  (+ bare https:// / www. autolink)
//   • Mention       → bare `@handle` (resolved via the message's
//                     `mentions[]` id list when available)
//   • Blockquote    → leading `> ` (adjacent lines merge)
//   • Code block    → ``` fence (verbatim, optional language label)
//   • Bulleted list → `- ` / `* ` (2-space indent per nesting level)
//   • Numbered list → `1. ` (2-space indent per nesting level)
//
// The parser is PURE (no React / RN imports) so it can be unit-tested
// and memoized cheaply by the renderer. It is intentionally tolerant:
// any construct it doesn't recognize falls through to literal text so
// nothing is ever dropped.

import { decodeBubbleBody } from '../decodeBubbleBody';

// ── Inline model ──────────────────────────────────────────────────────

export interface InlineMarks {
  readonly bold?: boolean;
  readonly italic?: boolean;
  readonly strike?: boolean;
  readonly code?: boolean;
}

export type MrkdwnInline =
  | ({ readonly type: 'text'; readonly text: string } & InlineMarks)
  | ({ readonly type: 'link'; readonly text: string; readonly url: string } & InlineMarks)
  | {
      readonly type: 'mention';
      /** Display text WITHOUT the leading `@` (may contain spaces). */
      readonly handle: string;
      /** Resolved account id when known, else the bare handle. */
      readonly userId: string;
    };

/**
 * A known mention's canonical display name + id, used to render
 * multi-word display names (`@aik 11`) as one pill. Supplied by the
 * tile from the message envelope's ranged `payload.mentions`.
 */
export interface KnownMention {
  readonly userId: string;
  readonly displayName: string;
}

// ── Block model ───────────────────────────────────────────────────────

export interface MrkdwnListItem {
  readonly inlines: ReadonlyArray<MrkdwnInline>;
  /** Nested list(s), already parsed. Empty when the item is a leaf. */
  readonly children: ReadonlyArray<MrkdwnBlock>;
}

export type MrkdwnBlock =
  // A run of consecutive plain lines. Each entry in `lines` is one
  // visual line; the renderer joins them with line breaks.
  | { readonly type: 'paragraph'; readonly lines: ReadonlyArray<ReadonlyArray<MrkdwnInline>> }
  | { readonly type: 'quote'; readonly lines: ReadonlyArray<ReadonlyArray<MrkdwnInline>> }
  | { readonly type: 'code'; readonly text: string; readonly language?: string | undefined }
  | { readonly type: 'list'; readonly ordered: boolean; readonly items: ReadonlyArray<MrkdwnListItem> };

// ─────────────────────────────────────────────────────────────────────

/** Shared inline-parse context threaded through blocks → inlines. */
interface InlineCtx {
  readonly mentions: ReadonlyArray<string>;
  readonly knownMentions: ReadonlyArray<KnownMention>;
}

// Opening fence: a run of ≥3 backticks, then an optional language/info
// string. The captured backtick run length is remembered so the block only
// closes on a fence AT LEAST that long — this is what lets a nested code
// block (an inner ``` inside the body) survive instead of prematurely
// closing the outer block. Mirrors the composer serializer's CommonMark
// variable-length fences.
const FENCE_OPEN_RE = /^(`{3,})(.*)$/;
// Closing fence: a line that is ONLY backticks (+ optional whitespace). A
// line like ```html has trailing non-space text, so it is body content, not
// a closer.
const FENCE_CLOSE_RE = /^(`{3,})\s*$/;
// A list line: optional 2-space-per-level indent, then a marker. The
// marker is `- ` / `* ` (bullet) or `<digits>. ` (ordered).
const LIST_RE = /^(\s*)(?:([-*])|(\d+)\.)\s+(.*)$/;
const QUOTE_RE = /^>\s?(.*)$/;

/**
 * Parse a message body (Slack mrkdwn) into a block tree. `mentions` is
 * the message's flat id/handle list — a bare `@handle` in the body is
 * resolved against it (falling back to the literal handle so it stays
 * highlighted + tappable even when the array didn't populate, matching
 * the legacy `parseMessageBody` contract).
 */
export function parseMrkdwn(
  rawBody: string,
  mentions: ReadonlyArray<string> = [],
  knownMentions: ReadonlyArray<KnownMention> = [],
): ReadonlyArray<MrkdwnBlock> {
  const body = rejoinSplitLinks(decodeBubbleBody(rawBody));
  // Sort known mentions longest-display-name-first so `@John Doe`
  // claims its span before a bare `@John` prefix can match inside it.
  const sortedMentions =
    knownMentions.length > 0
      ? [...knownMentions].sort(
          (a, b) => b.displayName.length - a.displayName.length,
        )
      : knownMentions;
  const ctx: InlineCtx = { mentions, knownMentions: sortedMentions };
  const lines = body.split('\n');
  const blocks: MrkdwnBlock[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i] ?? '';

    // ── Fenced code block ──────────────────────────────────────────
    const fence = FENCE_OPEN_RE.exec(line);
    if (fence !== null) {
      const fenceLen = (fence[1] ?? '```').length;
      const language = (fence[2] ?? '').trim();
      const codeLines: string[] = [];
      i += 1;
      let closed = false;
      while (i < lines.length) {
        const cur = lines[i] ?? '';
        // Close only on a fence at least as long as the opener — a shorter
        // (or non-bare) ``` run is body content, so an inner nested code
        // block round-trips instead of collapsing the outer block.
        const close = FENCE_CLOSE_RE.exec(cur);
        if (close !== null && (close[1] ?? '').length >= fenceLen) {
          closed = true;
          i += 1;
          break;
        }
        codeLines.push(cur);
        i += 1;
      }
      // Tolerate an unterminated fence: everything to EOF is the block.
      void closed;
      blocks.push({
        type: 'code',
        text: codeLines.join('\n'),
        ...(language.length > 0 ? { language } : {}),
      });
      continue;
    }

    // ── Blockquote (consecutive `> ` lines merge) ──────────────────
    if (QUOTE_RE.test(line)) {
      const quoteLines: ReadonlyArray<MrkdwnInline>[] = [];
      while (i < lines.length) {
        const m = QUOTE_RE.exec(lines[i] ?? '');
        if (m === null) break;
        quoteLines.push(parseInlines(m[1] ?? '', ctx));
        i += 1;
      }
      blocks.push({ type: 'quote', lines: quoteLines });
      continue;
    }

    // ── List (consecutive marker lines, nested by indent) ──────────
    if (LIST_RE.test(line)) {
      const consumed = parseList(lines, i, ctx);
      blocks.push(consumed.block);
      i = consumed.next;
      continue;
    }

    // ── Blank line — paragraph separator (skip; gap is visual) ─────
    if (line.trim().length === 0) {
      i += 1;
      continue;
    }

    // ── Paragraph (run of consecutive plain lines) ─────────────────
    const paraLines: ReadonlyArray<MrkdwnInline>[] = [];
    while (i < lines.length) {
      const cur = lines[i] ?? '';
      if (
        cur.trim().length === 0 ||
        FENCE_OPEN_RE.test(cur) ||
        QUOTE_RE.test(cur) ||
        LIST_RE.test(cur)
      ) {
        break;
      }
      paraLines.push(parseInlines(cur, ctx));
      i += 1;
    }
    blocks.push({ type: 'paragraph', lines: paraLines });
  }

  return blocks;
}

// A `[label](url)` whose label/url got split across visual lines (a
// common artifact of pasting a formatted search result or a wrapped
// link). `[^\]]`/`[^)]` already span newlines in JS regex, so the only
// reason the per-line block parser misses these is the newline; this
// pre-pass collapses the INTERNAL newlines so the link lands on one
// line and renders as a single link.
const SPLIT_LINK_RE = /\[([^\]]*?)\]\(([^)\s]+)\)/g;

function rejoinSplitLinks(body: string): string {
  if (body.indexOf('\n') === -1 || body.indexOf('[') === -1) return body;
  return body.replace(SPLIT_LINK_RE, (match, label: string, url: string) => {
    if (match.indexOf('\n') === -1) return match; // already single-line
    // Don't reach across a paragraph break / blank line, and cap the
    // label so a pathological match can't swallow the whole message.
    if (/\n[ \t]*\n/.test(label) || label.length > 500) return match;
    const cleanLabel = label.replace(/[ \t]*\n[ \t]*/g, ' ').trim();
    return `[${cleanLabel}](${url})`;
  });
}

// ── List parsing (nested via 2-space indent) ──────────────────────────

interface ParsedList {
  readonly block: MrkdwnBlock;
  readonly next: number;
}

/**
 * Parse a maximal list starting at `start`. Items deeper than the
 * list's base indent are recursively attached as nested-list children
 * of the preceding item (2 spaces == one level, matching the
 * serializer). A change in marker family (bullet ↔ ordered) at the
 * SAME indent ends the current list so the renderer can show the
 * correct markers.
 */
function parseList(
  lines: ReadonlyArray<string>,
  start: number,
  ctx: InlineCtx,
): ParsedList {
  const first = LIST_RE.exec(lines[start] ?? '');
  // `first` is guaranteed non-null by the caller's `LIST_RE.test`.
  const baseIndent = (first?.[1] ?? '').length;
  const baseOrdered = first?.[3] !== undefined;

  const items: MrkdwnListItem[] = [];
  let i = start;

  while (i < lines.length) {
    const m = LIST_RE.exec(lines[i] ?? '');
    if (m === null) break;
    const indent = (m[1] ?? '').length;
    const ordered = m[3] !== undefined;

    if (indent < baseIndent) break; // belongs to an outer list
    if (indent === baseIndent && ordered !== baseOrdered) break; // sibling of a different family

    if (indent > baseIndent) {
      // Deeper — recurse and attach to the previous item as a child.
      const nested = parseList(lines, i, ctx);
      const prev = items[items.length - 1];
      if (prev !== undefined) {
        items[items.length - 1] = {
          inlines: prev.inlines,
          children: [...prev.children, nested.block],
        };
      } else {
        // No parent item (malformed) — treat the nested block as a
        // standalone item so content is preserved.
        items.push({ inlines: [], children: [nested.block] });
      }
      i = nested.next;
      continue;
    }

    // Same level — a new item.
    items.push({ inlines: parseInlines(m[4] ?? '', ctx), children: [] });
    i += 1;
  }

  return { block: { type: 'list', ordered: baseOrdered, items }, next: i };
}

// ── Inline parsing ────────────────────────────────────────────────────

const EMPHASIS: Readonly<Record<string, keyof InlineMarks>> = {
  '*': 'bold',
  _: 'italic',
  '~': 'strike',
};

const URL_RE = /^(https?:\/\/[^\s<>"'`)\]]+|www\.[^\s<>"'`)\]]+)/i;
const LINK_RE = /^\[([^\]]*)\]\(([^)\s]+)\)/;
const MENTION_RE = /^@(\w[\w.-]*)/;

// Bare-domain autolink (no scheme / no www.). Group 1 = the TLD, checked
// against a curated set so prose like `Node.js`, `3.14`, `file.txt`
// doesn't become a link. Mirrors the composer's on-type autolinker
// (`enriched-text-input` autoFormat) so both ends agree on what's a link.
const BARE_DOMAIN_RE =
  /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+([a-z]{2,24})(?:\/[^\s<>"'`)\]]*)?/i;
const COMMON_TLDS: ReadonlySet<string> = new Set([
  'com', 'org', 'net', 'io', 'co', 'dev', 'app', 'ai', 'xyz', 'me', 'gg',
  'tv', 'info', 'biz', 'online', 'site', 'tech', 'store', 'blog', 'news',
  'cloud', 'page', 'link', 'live', 'chat', 'edu', 'gov', 'mil', 'uk', 'us',
  'ca', 'de', 'fr', 'es', 'it', 'nl', 'ru', 'jp', 'cn', 'in', 'br', 'au',
  'se', 'no', 'fi', 'dk', 'ch', 'at', 'be', 'pl', 'pt', 'cz', 'gr', 'ie',
  'nz', 'za', 'mx', 'kr', 'sg', 'hk', 'tw', 'ae', 'sa', 'tr', 'ua', 'id',
  'ph', 'my', 'th', 'vn',
]);

function isWordChar(ch: string | undefined): boolean {
  return ch !== undefined && /[A-Za-z0-9]/.test(ch);
}

/** Human-friendly link label: drop the scheme + a trailing slash. */
function cleanLinkLabel(url: string): string {
  return url.replace(/^https?:\/\//i, '').replace(/\/$/, '');
}

function normalizeUrl(raw: string): string {
  return /^www\./i.test(raw) ? `https://${raw}` : raw;
}

/**
 * Parse one logical line into inline runs. `marks` carries the active
 * emphasis set inward through recursion so nested spans
 * (`*_bold italic_*`) accumulate flags.
 */
function parseInlines(
  text: string,
  ctx: InlineCtx,
  marks: InlineMarks = {},
): MrkdwnInline[] {
  const { mentions, knownMentions } = ctx;
  const out: MrkdwnInline[] = [];
  let buf = '';
  let i = 0;

  const flush = (): void => {
    if (buf.length > 0) {
      out.push({ type: 'text', text: buf, ...marks });
      buf = '';
    }
  };

  while (i < text.length) {
    const ch = text[i]!;
    const prev = i > 0 ? text[i - 1] : undefined;
    const rest = text.slice(i);

    // ── Inline code — verbatim, highest precedence, no inner markup.
    if (ch === '`' && !marks.code) {
      const close = text.indexOf('`', i + 1);
      if (close > i + 1) {
        flush();
        out.push({ type: 'text', text: text.slice(i + 1, close), ...marks, code: true });
        i = close + 1;
        continue;
      }
    }

    // ── Markdown link [label](url).
    if (ch === '[') {
      const lm = LINK_RE.exec(rest);
      if (lm !== null) {
        flush();
        const label = lm[1] ?? '';
        const url = normalizeUrl(lm[2] ?? '');
        const display =
          label.length > 0 && !/^https?:\/\//i.test(label)
            ? label
            : cleanLinkLabel(url);
        out.push({ type: 'link', text: display, url, ...stripCode(marks) });
        i += lm[0].length;
        continue;
      }
    }

    // ── Bare URL autolink (http(s):// or www.).
    if ((ch === 'h' || ch === 'H' || ch === 'w' || ch === 'W') && !isWordChar(prev)) {
      const um = URL_RE.exec(rest);
      if (um !== null) {
        flush();
        const raw = um[0];
        out.push({
          type: 'link',
          text: cleanLinkLabel(normalizeUrl(raw)),
          url: normalizeUrl(raw),
          ...stripCode(marks),
        });
        i += raw.length;
        continue;
      }
    }

    // ── Bare-domain autolink (test.com, example.co.uk/path). Matches the
    // composer's curated-TLD gate so `test.com` renders as a link in the
    // tile even when it was sent as plain text (e.g. Enter with no
    // trailing space), while `Node.js` / `3.14` / `file.txt` stay text.
    // Skipped inside inline code.
    if (
      !marks.code &&
      /[a-z0-9]/i.test(ch) &&
      !isWordChar(prev) &&
      prev !== '.' &&
      prev !== '@'
    ) {
      const bm = BARE_DOMAIN_RE.exec(rest);
      if (bm !== null && COMMON_TLDS.has((bm[1] ?? '').toLowerCase())) {
        flush();
        const raw = bm[0];
        out.push({
          type: 'link',
          text: raw,
          url: `https://${raw}`,
          ...stripCode(marks),
        });
        i += raw.length;
        continue;
      }
    }

    // ── @mention.
    if (ch === '@' && !isWordChar(prev) && !marks.code) {
      // Known mentions first (longest display name wins) so a
      // multi-word display name (`@aik 11`) renders as ONE pill. The
      // boundary after the name must not continue a word token, so a
      // longer name isn't matched by a shorter prefix.
      let matched = false;
      for (const km of knownMentions) {
        const needle = `@${km.displayName}`;
        if (
          rest.length >= needle.length &&
          rest.slice(0, needle.length).toLowerCase() === needle.toLowerCase()
        ) {
          const after = rest[needle.length];
          if (after === undefined || !isWordChar(after)) {
            flush();
            out.push({ type: 'mention', handle: km.displayName, userId: km.userId });
            i += needle.length;
            matched = true;
            break;
          }
        }
      }
      if (matched) continue;
      // Fallback: single-word `@handle` (resolved against the userId list).
      const mm = MENTION_RE.exec(rest);
      if (mm !== null) {
        const handle = mm[1] ?? '';
        const userId =
          mentions.find((m) => m === handle || m.endsWith(handle)) ?? handle;
        flush();
        out.push({ type: 'mention', handle, userId });
        i += mm[0].length;
        continue;
      }
    }

    // ── Emphasis (*bold*, _italic_, ~strike~), nestable.
    const mark = EMPHASIS[ch];
    if (mark !== undefined && !marks[mark]) {
      const close = findEmphasisClose(text, i, ch);
      if (close > i) {
        flush();
        const inner = text.slice(i + 1, close);
        out.push(...parseInlines(inner, ctx, { ...marks, [mark]: true }));
        i = close + 1;
        continue;
      }
    }

    buf += ch;
    i += 1;
  }

  flush();
  return out;
}

/** Links/mentions can't be inline-code; drop that flag when crossing in. */
function stripCode(marks: InlineMarks): InlineMarks {
  if (!marks.code) return marks;
  const { code: _drop, ...rest } = marks;
  return rest;
}

/**
 * Given an opening emphasis delimiter `ch` at `open`, return the index
 * of its matching close, or -1. Flanking rules (mrkdwn-flavored):
 *   • opener must be at a left boundary (start / non-word char before)
 *     and be followed by a non-space, non-same-delimiter char,
 *   • closer must be preceded by a non-space, non-same-delimiter char.
 * These mirror the composer's auto-format guards so what the editor
 * produced round-trips back to the same spans.
 */
function findEmphasisClose(text: string, open: number, ch: string): number {
  const before = open > 0 ? text[open - 1] : undefined;
  // Left flank: don't open mid-word (e.g. the `*` in `2*3`).
  if (isWordChar(before)) return -1;
  const next = text[open + 1];
  if (next === undefined || next === ' ' || next === '\t' || next === ch) return -1;

  for (let j = open + 1; j < text.length; j++) {
    if (text[j] !== ch) continue;
    const pc = text[j - 1];
    if (pc === ' ' || pc === '\t' || pc === ch) continue; // not a valid closer
    if (j === open + 1) continue; // empty span
    return j;
  }
  return -1;
}
